import cn from "classnames"
import React from "react"
import { type Sign as ExampleSign } from "@/api/graphql"
import Icons from "@/images/signomatic/objects"
import { useMinimaticLoaded } from "@/signomatic/app/minimatic"
import type Sign from "@/signomatic/app/sign"

interface ButtonProps {
  name: string
  active: boolean
  onClick: (e: React.FormEvent) => void
}

const Button: React.FC<ButtonProps> = ({ name, active, onClick }) => {
  return (
    <button
      className={cn(
        name,
        "button button-medium icon-only full-width no-hover-mobile no-focus mb-0 h-10 w-10 max-w-[40px]",
        {
          active,
          "button-secondary": active,
          "button-subtle": !active,
        }
      )}
      onClick={onClick}
      type="button"
    >
      {Icons[name] ? React.createElement(Icons[name]) : ""}
    </button>
  )
}

interface TextAlign {
  text1?: string | null
  text2?: string | null
  text3?: string | null
}

export interface MinimaticAlignmentConfiguration {
  textAlign?: TextAlign
}

export type MinimaticAlignmentSign = Pick<ExampleSign, "text1" | "text2" | "text3">

interface MinimaticAlignmentProps {
  minimatic: Sign
  sign?: MinimaticAlignmentSign
  textAlignConfig?: TextAlign
  update: (config: Partial<MinimaticAlignmentConfiguration>) => void
}

export const MinimaticAlignment: React.FC<MinimaticAlignmentProps> = ({ minimatic, sign, textAlignConfig, update }) => {
  const loaded = useMinimaticLoaded(minimatic)

  if (!loaded || !sign || !textAlignConfig) {
    return null
  }

  return (
    <div className="minimatic-alignment flex flex-col gap-y-2">
      <div className="text-1">
        <p className="paragraph-sm mb-0 leading-6">Text #1: {sign.text1}</p>
        <div className="flex">
          <Button
            name="justify_left"
            active={textAlignConfig.text1 === "left"}
            onClick={() => update({ textAlign: { text1: "left" } })}
          />
          <Button
            name="justify_center"
            active={textAlignConfig.text1 === "center"}
            onClick={() => update({ textAlign: { text1: "center" } })}
          />
          <Button
            name="justify_right"
            active={textAlignConfig.text1 === "right"}
            onClick={() => update({ textAlign: { text1: "right" } })}
          />
        </div>
      </div>

      {sign.text2 && (
        <div className="text-2">
          <p className="paragraph-sm mb-0 leading-6">Text #2: {sign.text2}</p>
          <div className="flex">
            <Button
              name="justify_left"
              active={textAlignConfig.text2 === "left"}
              onClick={() => update({ textAlign: { text2: "left" } })}
            />
            <Button
              name="justify_center"
              active={textAlignConfig.text2 === "center"}
              onClick={() => update({ textAlign: { text2: "center" } })}
            />
            <Button
              name="justify_right"
              active={textAlignConfig.text2 === "right"}
              onClick={() => update({ textAlign: { text2: "right" } })}
            />
          </div>
        </div>
      )}

      {sign.text3 && (
        <div className="text-3">
          <p className="paragraph-sm mb-0 leading-6">Text #3: {sign.text3}</p>
          <div className="flex">
            <Button
              name="justify_left"
              active={textAlignConfig.text3 === "left"}
              onClick={() => update({ textAlign: { text3: "left" } })}
            />
            <Button
              name="justify_center"
              active={textAlignConfig.text3 === "center"}
              onClick={() => update({ textAlign: { text3: "center" } })}
            />
            <Button
              name="justify_right"
              active={textAlignConfig.text3 === "right"}
              onClick={() => update({ textAlign: { text3: "right" } })}
            />
          </div>
        </div>
      )}
    </div>
  )
}
